<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import format from 'date-fns/format';
import fromUnixTime from 'date-fns/fromUnixTime';
import { useAlert } from 'dashboard/composables';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import {
  downloadCsvFile,
  generateFileName,
} from 'dashboard/helper/downloadHelper';
import { conversationUrl, frontendURL } from 'dashboard/helper/URLHelper';
import TableFooter from 'dashboard/components/widgets/TableFooter.vue';
import Avatar from 'next/avatar/Avatar.vue';
import BarChart from 'shared/components/charts/BarChart.vue';
import {
  buildDatasetHoverDimmingHandler,
  buildIntercomLikeBarTooltip,
  removeTooltipEl,
} from 'shared/components/charts/barInteractions';
import SLAReportsAPI from 'dashboard/api/slaReports';
import CompactDateInboxFilters from './components/CompactDateInboxFilters.vue';

const { t } = useI18n();
const store = useStore();
const showAlert = useAlert;

const accountId = useMapGetter('getCurrentAccountId');
const isFeatureEnabledOnAccount = useMapGetter(
  'accounts/isFeatureEnabledonAccount'
);
const slaPolicies = useMapGetter('sla/getSLA');

const isTeamsEnabled = computed(() =>
  isFeatureEnabledOnAccount.value?.(
    accountId.value,
    FEATURE_FLAGS.TEAM_MANAGEMENT
  )
);

const isLoadingOverview = ref(false);
const isLoadingBreaches = ref(false);
const generatedAt = ref(null);
const errorMessage = ref('');
const currentPage = ref(1);
const showPolicyMenu = ref(false);
const policyMenuRef = ref(null);
const latestOverviewRefreshToken = ref(0);
const latestBreachesRefreshToken = ref(0);

const filters = ref({
  from: 0,
  to: 0,
  assigned_agent_id: [],
  inbox_id: null,
  team_id: null,
  sla_policy_id: null,
  label_list: null,
});

const overview = ref({
  summary: {},
  current_period: {},
  series: [],
  policies: [],
});

const breachPayload = ref([]);
const breachMeta = ref({
  count: 0,
  current_page: 1,
});

const chartRows = computed(() => overview.value?.series || []);
const summary = computed(() => overview.value?.summary || {});
const policyRows = computed(() => overview.value?.policies || []);
const hasChartData = computed(() =>
  chartRows.value.some(
    row =>
      Number(row.frt_count || 0) > 0 ||
      Number(row.nrt_count || 0) > 0 ||
      Number(row.rt_count || 0) > 0
  )
);

const selectedPolicy = computed(
  () =>
    (slaPolicies.value || []).find(
      policy => policy.id === filters.value.sla_policy_id
    ) || null
);

const policyChipLabel = computed(
  () => selectedPolicy.value?.name || 'All SLA policies'
);

const currentPeriodLabel = computed(() => {
  const period = overview.value?.current_period || {};
  if (!period.from || !period.to) return '-';
  return `${format(fromUnixTime(period.from), 'dd MMM yy')} - ${format(
    fromUnixTime(period.to),
    'dd MMM yy'
  )}`;
});

const generatedAtLabel = computed(() =>
  generatedAt.value
    ? generatedAt.value.toLocaleString()
    : t('LAST_7_DAYS_REPORT.NOT_GENERATED_YET')
);

const tooltipId = 'sla-miss-events-tooltip';

const createOverviewRefreshToken = () => {
  latestOverviewRefreshToken.value += 1;
  return latestOverviewRefreshToken.value;
};

const createBreachesRefreshToken = () => {
  latestBreachesRefreshToken.value += 1;
  return latestBreachesRefreshToken.value;
};

const isLatestOverviewRefresh = refreshToken =>
  refreshToken === latestOverviewRefreshToken.value;
const isLatestBreachesRefresh = refreshToken =>
  refreshToken === latestBreachesRefreshToken.value;

const chartCollection = computed(() => ({
  labels: chartRows.value.map(row =>
    format(fromUnixTime(row.current_timestamp), 'dd MMM')
  ),
  datasets: [
    {
      label: 'First response misses',
      data: chartRows.value.map(row => Number(row.frt_count || 0)),
      backgroundColor: '#f59e0b',
      borderRadius: 4,
      categoryPercentage: 0.72,
      barPercentage: 0.95,
      stack: 'sla-misses',
    },
    {
      label: 'Next response misses',
      data: chartRows.value.map(row => Number(row.nrt_count || 0)),
      backgroundColor: '#2563eb',
      borderRadius: 4,
      categoryPercentage: 0.72,
      barPercentage: 0.95,
      stack: 'sla-misses',
    },
    {
      label: 'Resolution misses',
      data: chartRows.value.map(row => Number(row.rt_count || 0)),
      backgroundColor: '#ef4444',
      borderRadius: 4,
      categoryPercentage: 0.72,
      barPercentage: 0.95,
      stack: 'sla-misses',
    },
  ],
}));

const chartOptions = computed(() => ({
  responsive: true,
  maintainAspectRatio: false,
  animation: { duration: 0 },
  interaction: { mode: 'nearest', intersect: true },
  onHover: buildDatasetHoverDimmingHandler(),
  plugins: {
    legend: { display: false },
    tooltip: {
      enabled: false,
      displayColors: false,
      external: buildIntercomLikeBarTooltip({
        tooltipId,
        titleForIndex: index => {
          const row = chartRows.value[index];
          if (!row) return '';
          return format(fromUnixTime(row.current_timestamp), "dd MMM''yy");
        },
        seriesLabelForDataset: datasetIndex => {
          if (datasetIndex === 0) return 'First response misses';
          if (datasetIndex === 1) return 'Next response misses';
          if (datasetIndex === 2) return 'Resolution misses';
          return '';
        },
        percentDigits: 2,
        showPercent: true,
      }),
    },
  },
  scales: {
    x: {
      stacked: true,
    },
    y: {
      stacked: true,
      beginAtZero: true,
      ticks: {
        callback: value => Number(value).toLocaleString(),
      },
    },
  },
}));

const summaryCards = computed(() => [
  {
    key: 'final-hit-rate',
    label: 'Final SLA hit rate',
    value: formatPercent(summary.value.final_hit_rate),
    hint: `${formatInteger(summary.value.resolved_count)} resolved conversations`,
  },
  {
    key: 'monitored',
    label: 'Monitored conversations',
    value: formatInteger(summary.value.total_monitored_count),
    hint: `${formatInteger(summary.value.active_count)} still active`,
  },
  {
    key: 'breached',
    label: 'Breached conversations',
    value: formatInteger(summary.value.breached_count),
    hint: `${formatPercent(summary.value.breach_rate)} of monitored conversations`,
  },
  {
    key: 'open-breached',
    label: 'Open breached',
    value: formatInteger(summary.value.open_breached_count),
    hint: 'Still unresolved and already breached',
  },
]);

const missTypeCards = computed(() => [
  {
    key: 'frt',
    label: 'First response misses',
    value: formatInteger(summary.value.first_response_miss_count),
    tone: 'warning',
  },
  {
    key: 'nrt',
    label: 'Next response misses',
    value: formatInteger(summary.value.next_response_miss_count),
    tone: 'brand',
  },
  {
    key: 'rt',
    label: 'Resolution misses',
    value: formatInteger(summary.value.resolution_miss_count),
    tone: 'danger',
  },
]);

const breachRows = computed(() =>
  (breachPayload.value || []).map(item => {
    const events = item.sla_events || [];
    const sortedEvents = [...events].sort(
      (a, b) => Number(a.created_at || 0) - Number(b.created_at || 0)
    );
    const latestEvent = sortedEvents.at(-1);

    return {
      ...item,
      eventSummary: summarizeEvents(sortedEvents),
      latestMissedAt: latestEvent?.created_at || null,
    };
  })
);

const formatInteger = value => Number(value || 0).toLocaleString();
const formatPercent = value => `${Number(value || 0).toFixed(2)}%`;

const fetchOverview = async refreshToken => {
  if (!filters.value.from || !filters.value.to) return;

  isLoadingOverview.value = true;

  try {
    const response = await SLAReportsAPI.getOverview(filters.value);

    if (!isLatestOverviewRefresh(refreshToken)) return;

    overview.value = response.data || {
      summary: {},
      current_period: {},
      series: [],
      policies: [],
    };
    generatedAt.value = new Date();
  } catch {
    if (!isLatestOverviewRefresh(refreshToken)) return;
    errorMessage.value = t('SLA_REPORTS.FETCH_ERROR');
    overview.value = {
      summary: {},
      current_period: {},
      series: [],
      policies: [],
    };
  } finally {
    if (isLatestOverviewRefresh(refreshToken)) {
      isLoadingOverview.value = false;
    }
  }
};

const fetchBreaches = async (page, refreshToken) => {
  if (!filters.value.from || !filters.value.to) return;

  isLoadingBreaches.value = true;

  try {
    const response = await SLAReportsAPI.get({
      ...filters.value,
      page,
    });

    if (!isLatestBreachesRefresh(refreshToken)) return;

    breachPayload.value = response.data?.payload || [];
    breachMeta.value = response.data?.meta || {
      count: 0,
      current_page: page,
    };
  } catch {
    if (!isLatestBreachesRefresh(refreshToken)) return;
    errorMessage.value = t('SLA_REPORTS.FETCH_ERROR');
    breachPayload.value = [];
    breachMeta.value = {
      count: 0,
      current_page: page,
    };
  } finally {
    if (isLatestBreachesRefresh(refreshToken)) {
      isLoadingBreaches.value = false;
    }
  }
};

const fetchAll = async () => {
  if (!filters.value.from || !filters.value.to) return;

  errorMessage.value = '';
  const overviewRefreshToken = createOverviewRefreshToken();
  const breachesRefreshToken = createBreachesRefreshToken();

  await Promise.all([
    fetchOverview(overviewRefreshToken),
    fetchBreaches(currentPage.value, breachesRefreshToken),
  ]);
};

const onFilterChange = ({
  from,
  to,
  selectedAgents,
  selectedInbox,
  selectedTeam,
}) => {
  filters.value = {
    ...filters.value,
    from,
    to,
    assigned_agent_id: selectedAgents.map(agent => agent.id),
    inbox_id: selectedInbox?.id || null,
    team_id: selectedTeam?.id || null,
  };
  currentPage.value = 1;
  fetchAll();
};

const onSelectPolicy = policy => {
  filters.value = {
    ...filters.value,
    sla_policy_id: policy?.id || null,
  };
  showPolicyMenu.value = false;
  currentPage.value = 1;
  fetchAll();
};

const onPageChange = page => {
  currentPage.value = page;
  errorMessage.value = '';
  const breachesRefreshToken = createBreachesRefreshToken();
  fetchBreaches(page, breachesRefreshToken);
};

const downloadReports = async () => {
  try {
    const response = await SLAReportsAPI.download(filters.value);
    const fileName = generateFileName({
      type: 'sla',
      to: filters.value.to,
    });
    downloadCsvFile(fileName, response.data);
  } catch {
    showAlert(t('SLA_REPORTS.DOWNLOAD_FAILED'));
  }
};

const handleDocumentClick = event => {
  if (
    showPolicyMenu.value &&
    policyMenuRef.value &&
    !policyMenuRef.value.contains(event.target)
  ) {
    showPolicyMenu.value = false;
  }
};

const eventTypeLabel = type => {
  if (type === 'frt') return 'FRT';
  if (type === 'nrt') return 'NRT';
  if (type === 'rt') return 'Resolution';
  return String(type || '').toUpperCase();
};

const summarizeEvents = events => {
  const counts = events.reduce((result, event) => {
    const key = event.event_type;
    result[key] = (result[key] || 0) + 1;
    return result;
  }, {});

  return ['frt', 'nrt', 'rt']
    .filter(key => counts[key])
    .map(key => ({
      key,
      label: eventTypeLabel(key),
      count: counts[key],
    }));
};

const formatEventDate = timestamp => {
  if (!timestamp) return '-';
  return format(fromUnixTime(timestamp), 'dd MMM yy, HH:mm');
};

const breachStatusLabel = status => {
  if (status === 'active_with_misses') return 'Open breached';
  if (status === 'missed') return 'Final missed';
  if (status === 'active') return 'Active';
  if (status === 'hit') return 'Hit';
  return status || '-';
};

const breachStatusClass = status => {
  if (status === 'active_with_misses') return 'sla-pill sla-pill--danger';
  if (status === 'missed') return 'sla-pill sla-pill--warning';
  if (status === 'hit') return 'sla-pill sla-pill--success';
  return 'sla-pill';
};

const conversationHref = conversationId =>
  frontendURL(
    conversationUrl({
      accountId: accountId.value,
      id: conversationId,
    })
  );

onMounted(() => {
  store.dispatch('sla/get');
  document.addEventListener('click', handleDocumentClick);
});

onBeforeUnmount(() => {
  document.removeEventListener('click', handleDocumentClick);
  removeTooltipEl(tooltipId);
});
</script>

<!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
<template>
  <div class="sla-page">
    <div class="sla-shell">
      <div class="sla-header">
        <div class="sla-header-left">
          <h1 class="sla-title">{{ $t('SLA_REPORTS.HEADER') }}</h1>
        </div>
        <button class="sla-dl-btn" type="button" @click="downloadReports">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            width="16"
            height="16"
          >
            <path
              stroke="currentcolor"
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M4 17v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2m-8-1V4m0 12 5-5m-5 5-5-5"
            />
          </svg>
          <span>{{ $t('SLA_REPORTS.DOWNLOAD_SLA_REPORTS') }}</span>
        </button>
      </div>

      <div class="sla-toolbar">
        <div class="sla-toolbar-left">
          <CompactDateInboxFilters
            :show-add-filter="false"
            :show-inbox="true"
            :show-agents="true"
            :show-teams="isTeamsEnabled"
            @filter-change="onFilterChange"
          />
          <div ref="policyMenuRef" class="sla-filter-wrap">
            <button
              class="sla-filter-chip"
              type="button"
              :aria-expanded="showPolicyMenu ? 'true' : 'false'"
              @click.stop="showPolicyMenu = !showPolicyMenu"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                width="16"
                height="16"
              >
                <path
                  stroke="currentColor"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M4 6h16M7 12h10m-7 6h4"
                />
              </svg>
              <strong>Policy</strong>
              <span class="sla-filter-is">is</span>
              <span class="sla-filter-value">{{ policyChipLabel }}</span>
            </button>

            <div v-if="showPolicyMenu" class="sla-filter-menu" @click.stop>
              <div class="sla-filter-menu__title">SLA policy filter</div>
              <button
                class="sla-filter-menu__item"
                type="button"
                @click="onSelectPolicy(null)"
              >
                <span>All SLA policies</span>
              </button>
              <button
                v-for="policy in slaPolicies"
                :key="policy.id"
                class="sla-filter-menu__item"
                :class="{
                  'sla-filter-menu__item--active':
                    policy.id === filters.sla_policy_id,
                }"
                type="button"
                @click="onSelectPolicy(policy)"
              >
                <span class="sla-truncate">{{ policy.name }}</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div class="sla-content">
        <div v-if="errorMessage" class="sla-error">{{ errorMessage }}</div>

        <div class="sla-metric-row">
          <div v-for="card in summaryCards" :key="card.key" class="sla-metric">
            <div class="sla-metric-label">{{ card.label }}</div>
            <div class="sla-metric-value">
              {{ isLoadingOverview ? '—' : card.value }}
            </div>
            <div class="sla-metric-sub">{{ card.hint }}</div>
          </div>
        </div>

        <section class="sla-card">
          <div class="sla-card-header">
            <div class="sla-card-title-wrap">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                width="20"
                height="20"
              >
                <path
                  stroke="currentColor"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M4 19V5m0 14h16M8 15l3-3 2 2 5-6"
                />
              </svg>
              <div>
                <h2 class="sla-card-title">Miss events trend</h2>
                <p class="sla-card-subtitle">
                  Daily first response, next response, and resolution misses for
                  {{ currentPeriodLabel }}
                </p>
              </div>
            </div>
          </div>

          <div class="sla-section">
            <div class="sla-miss-type-row">
              <div
                v-for="card in missTypeCards"
                :key="card.key"
                class="sla-mini-metric"
                :class="`sla-mini-metric--${card.tone}`"
              >
                <div class="sla-mini-metric__label">{{ card.label }}</div>
                <div class="sla-mini-metric__value">
                  {{ isLoadingOverview ? '—' : card.value }}
                </div>
              </div>
            </div>

            <div class="sla-chart">
              <div v-if="isLoadingOverview" class="sla-empty">
                Loading SLA overview…
              </div>
              <div v-else-if="!hasChartData" class="sla-empty">
                No SLA miss events found for the selected range.
              </div>
              <BarChart
                v-else
                :collection="chartCollection"
                :chart-options="chartOptions"
              />
            </div>
          </div>
        </section>

        <section class="sla-card">
          <div class="sla-card-header">
            <div class="sla-card-title-wrap">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                width="20"
                height="20"
              >
                <path
                  stroke="currentColor"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M3 7h18M3 12h18M3 17h18"
                />
              </svg>
              <div>
                <h2 class="sla-card-title">Policy performance</h2>
                <p class="sla-card-subtitle">
                  Compare monitored conversations, final hit rate, and breach
                  reasons across SLA policies.
                </p>
              </div>
            </div>
          </div>

          <div class="sla-table-wrap">
            <table class="sla-table">
              <thead>
                <tr>
                  <th>Policy</th>
                  <th class="sla-th-num">Monitored</th>
                  <th class="sla-th-num">Final hit rate</th>
                  <th class="sla-th-num">Breached</th>
                  <th class="sla-th-num">FRT</th>
                  <th class="sla-th-num">NRT</th>
                  <th class="sla-th-num">Resolution</th>
                </tr>
              </thead>
              <tbody>
                <tr v-if="!policyRows.length && !isLoadingOverview">
                  <td colspan="7" class="sla-empty-cell">
                    No SLA policy data for the selected range.
                  </td>
                </tr>
                <tr v-for="row in policyRows" :key="`policy-${row.policy_id}`">
                  <td class="sla-td-policy">
                    <div class="sla-policy-name">{{ row.policy_name }}</div>
                    <div class="sla-policy-sub">
                      {{
                        formatInteger(
                          row.active_count + row.open_breached_count
                        )
                      }}
                      active
                    </div>
                  </td>
                  <td class="sla-td-num">
                    {{ formatInteger(row.total_count) }}
                  </td>
                  <td class="sla-td-num">
                    {{ formatPercent(row.final_hit_rate) }}
                  </td>
                  <td class="sla-td-num">
                    {{ formatInteger(row.breached_conversations_count) }}
                  </td>
                  <td class="sla-td-num">
                    {{ formatInteger(row.frt_events_count) }}
                  </td>
                  <td class="sla-td-num">
                    {{ formatInteger(row.nrt_events_count) }}
                  </td>
                  <td class="sla-td-num">
                    {{ formatInteger(row.rt_events_count) }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        <section class="sla-card">
          <div class="sla-card-header">
            <div class="sla-card-title-wrap">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                width="20"
                height="20"
              >
                <path
                  stroke="currentColor"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M8 7h8m-8 5h8m-8 5h5M5 3h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2z"
                />
              </svg>
              <div>
                <h2 class="sla-card-title">Breached conversations</h2>
                <p class="sla-card-subtitle">
                  Live list of conversations that already missed at least one
                  SLA threshold.
                </p>
              </div>
            </div>
          </div>

          <div class="sla-table-wrap">
            <table class="sla-table">
              <thead>
                <tr>
                  <th>Conversation</th>
                  <th>Policy</th>
                  <th>Agent</th>
                  <th>Breach type</th>
                  <th>Status</th>
                  <th>Last missed at</th>
                </tr>
              </thead>
              <tbody>
                <tr v-if="isLoadingBreaches">
                  <td colspan="6" class="sla-empty-cell">
                    Loading breached conversations…
                  </td>
                </tr>
                <tr v-else-if="!breachRows.length">
                  <td colspan="6" class="sla-empty-cell">
                    {{ $t('SLA_REPORTS.NO_RECORDS') }}
                  </td>
                </tr>
                <tr
                  v-for="row in breachRows"
                  :key="`breach-${row.applied_sla.id}`"
                >
                  <td>
                    <div class="sla-conversation">
                      <a
                        :href="conversationHref(row.conversation.id)"
                        class="sla-conversation__id"
                      >
                        #{{ row.conversation.id }}
                      </a>
                      <div class="sla-conversation__meta">
                        <div class="sla-conversation__name">
                          {{
                            row.conversation.contact?.name || 'Unknown contact'
                          }}
                        </div>
                        <div
                          v-if="row.conversation.labels"
                          class="sla-conversation__labels"
                        >
                          {{ row.conversation.labels }}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td>
                    <div class="sla-policy-name">
                      {{ row.applied_sla.sla_name }}
                    </div>
                  </td>
                  <td>
                    <div v-if="row.conversation.assignee" class="sla-agent">
                      <Avatar
                        :src="row.conversation.assignee.thumbnail"
                        :name="row.conversation.assignee.name"
                        :size="28"
                        rounded-full
                        hide-offline-status
                      />
                      <span>{{ row.conversation.assignee.name }}</span>
                    </div>
                    <span v-else class="sla-muted">Unassigned</span>
                  </td>
                  <td>
                    <div class="sla-event-list">
                      <span
                        v-for="event in row.eventSummary"
                        :key="`${row.applied_sla.id}-${event.key}`"
                        class="sla-event-chip"
                      >
                        {{ event.label }} x{{ event.count }}
                      </span>
                    </div>
                  </td>
                  <td>
                    <span
                      :class="breachStatusClass(row.applied_sla.sla_status)"
                    >
                      {{ breachStatusLabel(row.applied_sla.sla_status) }}
                    </span>
                  </td>
                  <td>{{ formatEventDate(row.latestMissedAt) }}</td>
                </tr>
              </tbody>
            </table>
          </div>

          <TableFooter
            v-if="Number(breachMeta.count) > 25"
            :current-page="Number(breachMeta.current_page || currentPage)"
            :total-count="Number(breachMeta.count || 0)"
            :page-size="25"
            @page-change="onPageChange"
          />
        </section>

        <p class="sla-footer-note">
          This report was generated on {{ generatedAtLabel }}.
        </p>
      </div>
    </div>
  </div>
</template>
<!-- eslint-enable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->

<style scoped>
.sla-page {
  min-height: 100vh;
  min-height: 100dvh;
  width: 100%;
  box-sizing: border-box;
  background: #ffffff;
  color: #111827;
}
.sla-shell {
  min-width: 650px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #ffffff;
  overflow: visible;
}

.sla-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 58px;
  padding: 0 24px;
  border-bottom: 1px solid #eceff3;
  position: sticky;
  top: 0;
  z-index: 50;
  background: #ffffff;
}

.sla-header-left {
  display: flex;
  align-items: center;
  gap: 0;
}
.sla-title {
  margin: 0;
  font-size: 18px !important;
  line-height: 24px;
  font-weight: 600;
  letter-spacing: -0.02em;
  color: #111827;
}
.sla-dl-btn {
  min-width: 160px;
  height: 36px;
  border-radius: 8px;
  border: 1px solid #d1d5db;
  background: #ffffff;
  color: #111827;
  padding: 0 14px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
}
.sla-dl-btn:hover {
  background: #f9fafb;
}

.sla-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 24px 8px;
  background: #ffffff;
  box-shadow: rgba(0, 0, 0, 0.3) 0 3px 10px -6px;
  position: sticky;
  top: 58px;
  z-index: 49;
}

.sla-toolbar-left {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  min-width: 0;
  flex: 1 1 auto;
}
.sla-filter-wrap {
  position: relative;
}
.sla-filter-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 34px;
  padding: 0 12px;
  border-radius: 8px;
  border: 1px solid #d9dde3;
  background: #f3f4f6;
  color: #111827;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
}
.sla-filter-is {
  color: #111827;
}
.sla-filter-value {
  color: #4b5563;
  max-width: 220px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.sla-filter-menu {
  position: absolute;
  top: calc(100% + 8px);
  left: 0;
  width: 280px;
  max-height: 360px;
  overflow: auto;
  padding: 8px;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  background: #ffffff;
  box-shadow: 0 20px 28px rgba(17, 24, 39, 0.14);
  z-index: 30;
}
.sla-filter-menu__title {
  margin: 0 0 6px;
  padding: 0 4px;
  font-size: 12px;
  font-weight: 600;
  color: #6b7280;
}
.sla-filter-menu__item {
  width: 100%;
  min-height: 34px;
  border: 0;
  border-radius: 8px;
  background: transparent;
  color: #111827;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 8px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
}
.sla-filter-menu__item:hover {
  background: #f3f4f6;
}
.sla-filter-menu__item--active {
  background: #eef2ff;
  color: #1d4ed8;
  font-weight: 600;
}

.sla-content {
  padding: 16px 32px 32px;
  display: flex;
  flex-direction: column;
  gap: 20px;
}
.sla-error {
  margin-bottom: -8px;
  border: 1px solid #fecaca;
  background: #fef2f2;
  color: #b91c1c;
  border-radius: 8px;
  padding: 8px 10px;
  font-size: 13px;
}

.sla-metric-row {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 12px;
}
.sla-metric {
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  background: #f8fafc;
  padding: 14px;
}
.sla-metric-label {
  font-size: 13px;
  font-weight: 600;
  color: #111827;
}
.sla-metric-value {
  margin-top: 10px;
  font-size: 28px;
  line-height: 32px;
  font-weight: 700;
  color: #111827;
}
.sla-metric-sub {
  margin-top: 6px;
  font-size: 12px;
  color: #6b7280;
}

.sla-card {
  border-radius: 4px;
  border: 1px solid #d8dee5;
  background: #ffffff;
  box-sizing: border-box;
  overflow: hidden;
}
.sla-card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 18px 20px;
  border-bottom: 1px solid #e5e7eb;
}
.sla-card-title-wrap {
  display: inline-flex;
  align-items: flex-start;
  gap: 8px;
  min-width: 0;
}
.sla-card-title {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: #111827;
}
.sla-card-subtitle {
  margin: 4px 0 0;
  font-size: 12px;
  color: #6b7280;
  line-height: 1.5;
}

.sla-section {
  padding: 16px 20px 20px;
}
.sla-miss-type-row {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
}
.sla-mini-metric {
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  background: #ffffff;
  padding: 12px;
}
.sla-mini-metric__label {
  font-size: 12px;
  color: #6b7280;
  font-weight: 600;
}
.sla-mini-metric__value {
  margin-top: 8px;
  font-size: 24px;
  font-weight: 700;
  color: #111827;
}
.sla-mini-metric--warning .sla-mini-metric__value {
  color: #d97706;
}
.sla-mini-metric--brand .sla-mini-metric__value {
  color: #2563eb;
}
.sla-mini-metric--danger .sla-mini-metric__value {
  color: #dc2626;
}

.sla-chart {
  height: 360px;
  margin-top: 16px;
}
.sla-empty {
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6b7280;
  font-size: 13px;
}

.sla-table-wrap {
  overflow-x: auto;
}
.sla-table {
  width: 100%;
  min-width: 980px;
  border-collapse: separate;
  border-spacing: 0;
  font-size: 13px;
  color: #424d57;
}
.sla-table th {
  padding: 10px 12px;
  text-align: left;
  font-weight: 700;
  color: #111827;
  border-bottom: 1px solid #e5e7eb;
  background: #f8fafc;
  white-space: nowrap;
}
.sla-table td {
  padding: 12px;
  border-bottom: 1px solid #edf1f5;
  vertical-align: middle;
  color: #111827;
}
.sla-th-num,
.sla-td-num {
  text-align: right;
  white-space: nowrap;
}
.sla-td-policy {
  min-width: 200px;
}
.sla-policy-name {
  font-weight: 700;
  color: #111827;
}
.sla-policy-sub {
  margin-top: 2px;
  font-size: 12px;
  color: #6b7280;
}
.sla-empty-cell {
  height: 140px;
  text-align: center;
  color: #6b7280;
}

.sla-conversation {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  min-width: 240px;
}
.sla-conversation__id {
  color: #111827;
  font-weight: 700;
  text-decoration: underline;
  white-space: nowrap;
}
.sla-conversation__meta {
  display: flex;
  flex-direction: column;
  min-width: 0;
}
.sla-conversation__name {
  font-weight: 600;
  color: #111827;
}
.sla-conversation__labels {
  margin-top: 4px;
  font-size: 12px;
  color: #6b7280;
  line-height: 1.4;
}

.sla-agent {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
}
.sla-agent span {
  color: #111827;
  font-weight: 600;
}
.sla-muted {
  color: #6b7280;
}

.sla-event-list {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  min-width: 160px;
}
.sla-event-chip {
  display: inline-flex;
  align-items: center;
  height: 24px;
  padding: 0 8px;
  border-radius: 999px;
  background: #eff6ff;
  color: #1d4ed8;
  font-size: 12px;
  font-weight: 700;
}

.sla-pill {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 24px;
  padding: 2px 8px;
  border-radius: 999px;
  background: #f3f4f6;
  color: #374151;
  font-size: 12px;
  font-weight: 700;
  white-space: nowrap;
}
.sla-pill--danger {
  background: #fef2f2;
  color: #b91c1c;
}
.sla-pill--warning {
  background: #fffbeb;
  color: #b45309;
}
.sla-pill--success {
  background: #f0fdf4;
  color: #15803d;
}

.sla-footer-note {
  margin-top: 4px;
  font-size: 12px;
  color: #6b7280;
  line-height: 1.5;
}
.sla-truncate {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

::global(body.dark) .sla-page,
::global(body.dark) .sla-shell,
::global(body.dark) .sla-card,
::global(body.dark) .sla-header,
::global(body.dark) .sla-toolbar,
::global(body.dark) .sla-table th,
::global(body.dark) .sla-table td,
::global(body.theme-mix) .sla-page,
::global(body.theme-mix) .sla-shell,
::global(body.theme-mix) .sla-card,
::global(body.theme-mix) .sla-header,
::global(body.theme-mix) .sla-toolbar,
::global(body.theme-mix) .sla-table th,
::global(body.theme-mix) .sla-table td {
  background: #ffffff !important;
  color: #111827 !important;
}

::global(body.dark) .sla-table th *,
::global(body.theme-mix) .sla-table th * {
  color: #111827 !important;
}

@media (max-width: 1200px) {
  .sla-metric-row {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 960px) {
  .sla-content {
    padding: 12px 16px 16px;
  }
  .sla-toolbar {
    padding: 10px 16px 8px;
  }
  .sla-miss-type-row {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 720px) {
  .sla-metric-row {
    grid-template-columns: 1fr;
  }
}
</style>
